import {
  AddressDto,
  ContactDto,
  ContactType,
  FunctionContactDto,
  GematikProfile,
  PartnerDto,
  PartnerRoleDto,
} from '../../models/profile.model';
import * as fromProfile from '../actions/profile.actions';
import { Action, ActionCreator, createReducer, on, ReducerTypes } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState, Update } from '@ngrx/entity';
import { AssignmentGroupIdentityExtendedDto } from '../../models/profile.model';

export const FEATURE_KEY = 'profile';

export interface ProfileState extends EntityState<GematikProfile> {
  loading: boolean;
  loaded: boolean;
  error: boolean;
}

export interface AgiState extends EntityState<AssignmentGroupIdentityExtendedDto> {
  loading: boolean;
  loaded: boolean;
  error: boolean;
}

export interface AddressState extends EntityState<AddressDto> {
  loading: boolean;
  loaded: boolean;
  error: boolean;
}

export interface PartnerState extends EntityState<PartnerDto> {
  loading: boolean;
  loaded: boolean;
  error: boolean;
}

export interface PartnerRoleState extends EntityState<PartnerRoleDto> {
  loading: boolean;
  loaded: boolean;
  error: boolean;
}

export interface InternalContactState extends EntityState<ContactDto> {
  loading: boolean;
  loaded: boolean;
}

export interface ExternalContactState extends EntityState<ContactDto> {
  loading: boolean;
  loaded: boolean;
}

export interface FunctionContactState extends EntityState<ContactDto> {
  loading: boolean;
  loaded: boolean;
}

export interface OrgContactsState {
  internal: InternalContactState;
  external: ExternalContactState;
  function: FunctionContactState;
  loading: boolean;
  loaded: boolean;
  error: boolean;
}

export type ContactsState = { [orgId: string]: OrgContactsState };

export interface State {
  profile: ProfileState;
  agis: AgiState;
  addresses: AddressState;
  partners: PartnerState;
  partnerRoles: PartnerRoleState;
  contacts: ContactsState;
}

export const profileAdapter: EntityAdapter<GematikProfile> = createEntityAdapter<GematikProfile>();
export const agiAdapter: EntityAdapter<AssignmentGroupIdentityExtendedDto> =
  createEntityAdapter<AssignmentGroupIdentityExtendedDto>({
    sortComparer: (a: AssignmentGroupIdentityExtendedDto, b: AssignmentGroupIdentityExtendedDto) =>
      a.identityRole.localeCompare(b.identityRole),
  });
export const addressAdapter: EntityAdapter<AddressDto> = createEntityAdapter<AddressDto>();
export const partnerAdapter: EntityAdapter<PartnerDto> = createEntityAdapter<PartnerDto>();
export const partnerRoleAdapter: EntityAdapter<PartnerRoleDto> = createEntityAdapter<PartnerRoleDto>({
  selectId: (model: PartnerRoleDto) => `${model.id}.${model.partnerId}`,
});
export const internalContactAdapter: EntityAdapter<ContactDto> = createEntityAdapter<ContactDto>();
export const externalContactAdapter: EntityAdapter<ContactDto> = createEntityAdapter<ContactDto>();
export const functionContactAdapter: EntityAdapter<ContactDto> = createEntityAdapter<ContactDto>();

const createEmptyOrgContactsBucket = (): OrgContactsState => ({
  internal: internalContactAdapter.getInitialState({ loading: false, loaded: false }),
  external: externalContactAdapter.getInitialState({ loading: false, loaded: false }),
  function: functionContactAdapter.getInitialState({ loading: false, loaded: false }),
  loading: false,
  loaded: false,
  error: false,
});

function ensureOrgContactsBucket(contacts: ContactsState, orgId: string): OrgContactsState {
  return contacts[orgId] ?? createEmptyOrgContactsBucket();
}

export const initialProfileState: ProfileState = profileAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: false,
});
export const initialAgiState: AgiState = agiAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: false,
});
export const initialAddressesState: AddressState = addressAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: false,
});
export const initialPartnerState: PartnerState = partnerAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: false,
});
export const initialPartnerRoleState: PartnerRoleState = partnerRoleAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: false,
});

export const initialContactsState: ContactsState = {};

export const initialState: State = {
  profile: initialProfileState,
  agis: initialAgiState,
  addresses: initialAddressesState,
  partners: initialPartnerState,
  partnerRoles: initialPartnerRoleState,
  contacts: initialContactsState,
};

export const profileReducers: ReducerTypes<State, ActionCreator[]>[] = [
  on(fromProfile.LoadProfile, state => ({
    ...state,
    profile: { ...state.profile, loading: true, loaded: false, error: false },
  })),
  on(fromProfile.LoadProfileSuccess, (state, { payload }) => ({
    ...state,
    profile: profileAdapter.addOne(payload, { ...state.profile, loading: false, loaded: true }),
  })),
  on(fromProfile.LoadProfileFail, state => ({
    ...state,
    profile: { ...state.profile, loading: false, loaded: false, error: true },
  })),
  on(fromProfile.UpdateProfile, state => ({
    ...state,
    profile: { ...state.profile, loading: true, loaded: false },
  })),
  on(fromProfile.UpdateProfileSuccess, (state, { payload }) => ({
    ...state,
    profile: profileAdapter.updateOne(
      { id: payload.id, changes: payload },
      { ...state.profile, loading: false, loaded: true },
    ),
  })),
  on(fromProfile.UpdateProfileFail, state => ({
    ...state,
    profile: { ...state.profile, loading: false, loaded: false },
  })),
  on(fromProfile.UpdateUserActivity, state => ({
    ...state,
    profile: { ...state.profile, loading: true, loaded: false },
  })),
  on(fromProfile.UpdateUserActivitySuccess, (state, { payload }) => ({
    ...state,
    profile: profileAdapter.updateOne(
      { id: payload.id, changes: payload },
      { ...state.profile, loading: false, loaded: true },
    ),
  })),
  on(fromProfile.UpdateUserActivityFail, state => ({
    ...state,
    profile: { ...state.profile, loading: false, loaded: false },
  })),
  on(fromProfile.LoadAgis, state => ({
    ...state,
    agis: { ...state.agis, loading: true, loaded: false, error: false },
  })),
  on(fromProfile.LoadAgisSuccess, (state, { payload }) => ({
    ...state,
    agis: agiAdapter.setAll(payload, { ...state.agis, loading: false, loaded: true }),
  })),
  on(fromProfile.LoadAgisFail, state => ({
    ...state,
    agis: { ...state.agis, loading: false, loaded: false, error: true },
  })),
  on(fromProfile.LoadPartner, state => ({
    ...state,
    partners: { ...state.partners, loading: true, loaded: false, error: false },
  })),
  on(fromProfile.LoadPartnerSuccess, (state, { payload }) => ({
    ...state,
    partners: partnerAdapter.addOne(payload, { ...state.partners, loading: false, loaded: true }),
  })),
  on(fromProfile.LoadPartnerFail, state => ({
    ...state,
    partners: { ...state.partners, loading: false, loaded: false, error: true },
  })),
  on(fromProfile.UpdatePartner, state => ({
    ...state,
    partners: { ...state.partners, loading: true, loaded: false },
  })),
  on(fromProfile.UpdatePartnerSuccess, (state, { payload }) => {
    const update: Update<PartnerDto> = {
      id: payload.id,
      changes: { ...payload },
    };
    return {
      ...state,
      partners: partnerAdapter.updateOne(update, {
        ...state.partners,
        loading: false,
        loaded: true,
      }),
    };
  }),
  on(fromProfile.UpdatePartnerFail, state => ({
    ...state,
    partners: { ...state.partners, loading: false, loaded: false },
  })),
  on(fromProfile.LoadAgiAddresses, state => ({
    ...state,
    addresses: { ...state.addresses, loading: true, loaded: false, error: false },
  })),
  on(fromProfile.LoadAgiAddressesSuccess, (state, { payload }) => ({
    ...state,
    addresses: addressAdapter.addMany(payload, {
      ...state.addresses,
      loading: false,
      loaded: true,
    }),
  })),
  on(fromProfile.LoadAgiAddressesFail, state => ({
    ...state,
    addresses: { ...state.addresses, loading: false, loaded: false, error: true },
  })),
  on(fromProfile.AddAgiAddress, state => ({
    ...state,
    addresses: { ...state.addresses, loading: true, loaded: false },
  })),
  on(fromProfile.AddAgiAddressSuccess, (state, { payload }) => ({
    ...state,
    addresses: addressAdapter.addOne(payload, {
      ...state.addresses,
      loading: false,
      loaded: true,
    }),
  })),
  on(fromProfile.AddAgiAddressFail, state => ({
    ...state,
    addresses: { ...state.addresses, loading: false, loaded: false },
  })),
  on(fromProfile.UpdateAgiAddress, state => ({
    ...state,
    addresses: { ...state.addresses, loading: true, loaded: false },
  })),
  on(fromProfile.UpdateAgiAddressSuccess, (state, { payload }) => {
    const update: Update<AddressDto> = {
      id: payload.id,
      changes: { ...payload },
    };
    return {
      ...state,
      addresses: addressAdapter.updateOne(update, {
        ...state.addresses,
        loading: false,
        loaded: true,
      }),
    };
  }),
  on(fromProfile.UpdateAgiAddressFail, state => ({
    ...state,
    addresses: { ...state.addresses, loading: false, loaded: false },
  })),
  on(fromProfile.DeleteAgiAddress, state => ({
    ...state,
    addresses: { ...state.addresses, loading: true, loaded: false },
  })),
  on(fromProfile.DeleteAgiAddressSuccess, (state, { payload }) => ({
    ...state,
    addresses: addressAdapter.removeOne(payload.id, {
      ...state.addresses,
      loading: false,
      loaded: true,
    }),
  })),
  on(fromProfile.DeleteAgiAddressFail, state => ({
    ...state,
    addresses: { ...state.addresses, loading: false, loaded: false },
  })),
  on(fromProfile.LoadAgAddresses, state => ({
    ...state,
    addresses: { ...state.addresses, loading: true, loaded: false, error: false },
  })),
  on(fromProfile.LoadAgAddressesSuccess, (state, { payload }) => ({
    ...state,
    addresses: addressAdapter.addMany(payload, {
      ...state.addresses,
      loading: false,
      loaded: true,
    }),
  })),
  on(fromProfile.LoadAgAddressesFail, state => ({
    ...state,
    addresses: { ...state.addresses, loading: false, loaded: false, error: true },
  })),
  on(fromProfile.AddAgAddress, state => ({
    ...state,
    addresses: { ...state.addresses, loading: true, loaded: false },
  })),
  on(fromProfile.AddAgAddressSuccess, (state, { payload }) => ({
    ...state,
    addresses: addressAdapter.addOne(payload, {
      ...state.addresses,
      loading: false,
      loaded: true,
    }),
  })),
  on(fromProfile.AddAgAddressFail, state => ({
    ...state,
    addresses: { ...state.addresses, loading: false, loaded: false },
  })),
  on(fromProfile.UpdateAgAddress, state => ({
    ...state,
    addresses: { ...state.addresses, loading: true, loaded: false },
  })),
  on(fromProfile.UpdateAgAddressSuccess, (state, { payload }) => {
    const update: Update<AddressDto> = {
      id: payload.id,
      changes: { ...payload },
    };
    return {
      ...state,
      addresses: addressAdapter.updateOne(update, {
        ...state.addresses,
        loading: false,
        loaded: true,
      }),
    };
  }),
  on(fromProfile.UpdateAgAddressFail, state => ({
    ...state,
    addresses: { ...state.addresses, loading: false, loaded: false },
  })),
  on(fromProfile.DeleteAgAddress, state => ({
    ...state,
    addresses: { ...state.addresses, loading: true, loaded: false },
  })),
  on(fromProfile.DeleteAgAddressSuccess, (state, { payload }) => ({
    ...state,
    addresses: addressAdapter.removeOne(payload.id, {
      ...state.addresses,
      loading: false,
      loaded: true,
    }),
  })),
  on(fromProfile.DeleteAgAddressFail, state => ({
    ...state,
    addresses: { ...state.addresses, loading: false, loaded: false },
  })),
  on(fromProfile.LoadPartnerRoles, state => ({
    ...state,
    partnerRoles: { ...state.partnerRoles, loading: true, loaded: false, error: false },
  })),
  on(fromProfile.LoadPartnerRolesSuccess, (state, { payload }) => ({
    ...state,
    partnerRoles: partnerRoleAdapter.addMany(payload, {
      ...state.partnerRoles,
      loading: false,
      loaded: true,
    }),
  })),
  on(fromProfile.LoadPartnerRolesFail, state => ({
    ...state,
    partnerRoles: { ...state.partnerRoles, loading: false, loaded: false, error: true },
  })),
  on(fromProfile.LoadContacts, (state, { orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: true,
          loaded: false,
          error: false,
          internal: { ...bucket.internal, loading: true, loaded: false },
          external: { ...bucket.external, loading: true, loaded: false },
        },
      },
    };
  }),
  on(fromProfile.LoadContactsSuccess, (state, { payload }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, payload.orgId);
    const nextInternal = internalContactAdapter.setAll(payload.internalContacts ?? [], {
      ...bucket.internal,
      loading: false,
      loaded: true,
    });
    const nextExternal = externalContactAdapter.setAll(payload.externalContacts ?? [], {
      ...bucket.external,
      loading: false,
      loaded: true,
    });
    const nextFunction = functionContactAdapter.setAll(payload.functionContacts ?? [], {
      ...bucket.external,
      loading: false,
      loaded: true,
    });
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [payload.orgId]: {
          ...bucket,
          loading: false,
          loaded: true,
          internal: nextInternal,
          external: nextExternal,
          function: nextFunction,
        },
      },
    };
  }),
  on(fromProfile.LoadContactsFail, (state, { orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: false,
          error: true,
          internal: { ...bucket.internal, loading: false, loaded: false },
          external: { ...bucket.external, loading: false, loaded: false },
        },
      },
    };
  }),
  on(fromProfile.ToggleContactStatus, (state, { orgId, contactType }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    const ct = normalizeContactType(contactType);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: true,
          [ct]: { ...bucket[ct], loading: true, loaded: bucket[ct].loaded },
        },
      },
    };
  }),
  on(fromProfile.ToggleContactStatusSuccess, (state, { payload, orgId, contactType }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    const ct = normalizeContactType(contactType);
    const nextInternal =
      ct === 'internal'
        ? internalContactAdapter.upsertOne(payload, {
            ...bucket.internal,
            loading: false,
            loaded: true,
          })
        : { ...bucket.internal };
    const nextExternal =
      ct === 'external'
        ? externalContactAdapter.upsertOne(payload, {
            ...bucket.external,
            loading: false,
            loaded: true,
          })
        : { ...bucket.external };
    const nextFunction =
      ct === 'function'
        ? functionContactAdapter.upsertOne(payload, {
            ...bucket.function,
            loading: false,
            loaded: true,
          })
        : { ...bucket.function };
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: true,
          internal: nextInternal,
          external: nextExternal,
          function: nextFunction,
        },
      },
    };
  }),
  on(fromProfile.ToggleContactStatusFail, (state, { orgId, contactType }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    const ct = normalizeContactType(contactType);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: bucket.loaded,
          [ct]: { ...bucket[ct], loading: false, loaded: bucket[ct].loaded },
        },
      },
    };
  }),
  on(fromProfile.DeleteContact, (state, { orgId, contactType }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    const ct = normalizeContactType(contactType);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: true,
          [ct]: { ...bucket[ct], loading: true, loaded: bucket[ct].loaded },
        },
      },
    };
  }),
  on(fromProfile.DeleteContactSuccess, (state, { orgId, contact, contactType }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    const ct = normalizeContactType(contactType);
    const nextInternal =
      ct === 'internal'
        ? internalContactAdapter.upsertOne(contact, {
            ...bucket.internal,
            loading: false,
            loaded: true,
          })
        : { ...bucket.internal };
    const nextExternal =
      ct === 'external'
        ? externalContactAdapter.upsertOne(contact, {
            ...bucket.external,
            loading: false,
            loaded: true,
          })
        : { ...bucket.external };
    const nextFunction =
      ct === 'function'
        ? functionContactAdapter.upsertOne(contact, {
            ...bucket.function,
            loading: false,
            loaded: true,
          })
        : { ...bucket.function };
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: true,
          internal: nextInternal,
          external: nextExternal,
          function: nextFunction,
        },
      },
    };
  }),
  on(fromProfile.DeleteContactFail, (state, { orgId, contactType }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    const ct = normalizeContactType(contactType);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: bucket.loaded,
          [ct]: { ...bucket[ct], loading: false, loaded: bucket[ct].loaded },
        },
      },
    };
  }),
  on(fromProfile.ResetContactRootPartner, (state, { orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: true,
          internal: { ...bucket.internal, loading: true, loaded: bucket.internal.loaded },
          external: { ...bucket.external, loading: true, loaded: bucket.external.loaded },
        },
      },
    };
  }),
  on(fromProfile.ResetContactRootPartnerSuccess, (state, { orgId, contactId, external }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    const nextInternal = internalContactAdapter.removeOne(contactId, {
      ...bucket.internal,
      loading: false,
      loaded: true,
    });
    const nextExternal = externalContactAdapter.setAll(external ?? [], {
      ...bucket.external,
      loading: false,
      loaded: true,
    });
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: true,
          internal: nextInternal,
          external: nextExternal,
        },
      },
    };
  }),
  on(fromProfile.ResetContactRootPartnerFail, (state, { orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: bucket.loaded,
          internal: { ...bucket.internal, loading: false, loaded: bucket.internal.loaded },
          external: { ...bucket.external, loading: false, loaded: bucket.external.loaded },
        },
      },
    };
  }),
  on(fromProfile.SetContactRootPartner, (state, { orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: true,
          internal: { ...bucket.internal, loading: true, loaded: bucket.internal.loaded },
          external: { ...bucket.external, loading: true, loaded: bucket.external.loaded },
        },
      },
    };
  }),

  on(fromProfile.SetContactRootPartnerSuccess, (state, { contact, orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    const nextExternal = externalContactAdapter.removeOne(contact.id, {
      ...bucket.external,
      loading: false,
      loaded: true,
    });
    const nextInternal = internalContactAdapter.upsertOne(contact, {
      ...bucket.internal,
      loading: false,
      loaded: true,
    });
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: true,
          internal: nextInternal,
          external: nextExternal,
        },
      },
    };
  }),

  on(fromProfile.SetContactRootPartnerFail, (state, { orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: bucket.loaded,
          internal: { ...bucket.internal, loading: false, loaded: bucket.internal.loaded },
          external: { ...bucket.external, loading: false, loaded: bucket.external.loaded },
        },
      },
    };
  }),
  on(fromProfile.CreateFunctionContact, (state, { orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: true,
          function: { ...bucket.function, loading: true, loaded: bucket.function.loaded },
        },
      },
    };
  }),
  on(fromProfile.CreateFunctionContactSuccess, (state, { payload, orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    const nextFunction = functionContactAdapter.upsertOne(mapFunctionContactToContactDto(payload), {
      ...bucket.function,
      loading: false,
      loaded: true,
    });
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: true,
          function: nextFunction,
        },
      },
    };
  }),
  on(fromProfile.CreateFunctionContactFail, (state, { orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: bucket.loaded,
          function: { ...bucket.function, loading: false, loaded: bucket.function.loaded },
        },
      },
    };
  }),
  on(fromProfile.UpdateFunctionContact, (state, { orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: true,
          function: { ...bucket.function, loading: true, loaded: bucket.function.loaded },
        },
      },
    };
  }),
  on(fromProfile.UpdateFunctionContactSuccess, (state, { contactId, payload, orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    const nextFunction = functionContactAdapter.upsertOne(
      { ...mapFunctionContactToContactDto(payload), id: contactId },
      {
        ...bucket.function,
        loading: false,
        loaded: true,
      },
    );
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: true,
          function: nextFunction,
        },
      },
    };
  }),
  on(fromProfile.UpdateFunctionContactFail, (state, { orgId }) => {
    const bucket = ensureOrgContactsBucket(state.contacts, orgId);
    return {
      ...state,
      contacts: {
        ...state.contacts,
        [orgId]: {
          ...bucket,
          loading: false,
          loaded: bucket.loaded,
          function: { ...bucket.function, loading: false, loaded: bucket.function.loaded },
        },
      },
    };
  }),
];

const profileReducer = createReducer<State>(initialState, ...profileReducers);

export function reducer(state: State, action: Action) {
  return profileReducer(state, action);
}

function normalizeContactType(contactType: string): ContactType {
  const normalized = contactType?.toLowerCase();
  if (normalized === 'external' || normalized === 'function') {
    return normalized;
  }
  return 'internal';
}

function mapFunctionContactToContactDto(payload: FunctionContactDto): ContactDto {
  return {
    id: payload.id,
    salutation: '',
    firstName: '',
    lastName: payload.name,
    email: payload.email,
    telephone: payload.telephone,
    additionalTelephone: payload.additionalTelephone,
    organisation: '',
    keine: false,
    deleteDate: '',
    status: payload.status,
  };
}
